import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Heart, ShieldCheck, Droplets } from "lucide-react";
import { scrollToId } from "../lib/scroll";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.95, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Chip = ({ className, delay, children, testid }) => (
  <motion.div
    data-testid={testid}
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1, y: [0, -8, 0] }}
    transition={{
      opacity: { delay, duration: 0.5 },
      scale: { delay, duration: 0.5 },
      y: { delay, duration: 5, repeat: Infinity, ease: "easeInOut" },
    }}
    className={`absolute z-20 flex items-center gap-2 rounded-full bg-white/85 px-3 py-1.5 text-[10px] font-bold text-binky-blue shadow-lg shadow-binky-blue/10 backdrop-blur-md sm:px-4 sm:py-2 sm:text-xs ${className}`}
  >
    {children}
  </motion.div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 110]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-[72px]">
      <div className="pointer-events-none absolute -top-32 right-[-10%] h-[560px] w-[560px] rounded-full bg-binky-pink/15 blur-[120px]" />
      <div className="pointer-events-none absolute -left-40 top-1/3 h-[480px] w-[480px] rounded-full bg-binky-blue/15 blur-[120px]" />

      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-5 pb-14 pt-8 sm:px-8 lg:grid-cols-2 lg:gap-14 lg:pb-28 lg:pt-24">
        <div>
          <motion.div
            data-testid="hero-coming-soon-badge"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-5 inline-flex items-center gap-2.5 rounded-full border border-binky-pink/25 bg-binky-pinklight px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-binky-pink lg:mb-6"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-binky-pink opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-binky-pink" />
            </span>
            Coming Soon · Pre-Launch
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl"
          >
            <MaskedLine delay={0.1}>
              <span>
                <span className="text-binky-pink">Bi</span>
                <span className="text-binky-blue">NKY</span>
              </span>
            </MaskedLine>
            <MaskedLine delay={0.24}>Comfort</MaskedLine>
            <MaskedLine delay={0.38}>
              System<span className="text-binky-pink">.</span>
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subheading"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.7 }}
            className="mt-5 max-w-md text-base leading-relaxed text-slate-600 md:text-lg lg:mt-6"
          >
            A thoughtfully designed comfort system for babies and toddlers — helping comfort little
            ones when congested, with everything you need in one pack.{" "}
            <span className="font-bold text-binky-blue">From birth to 36 months.</span>
          </motion.p>
        </div>

        <motion.div
          style={{ y }}
          className="relative mx-auto w-full max-w-[420px] lg:row-span-2 lg:max-w-[520px]"
          data-testid="hero-product-visual"
        >
          <div className="absolute inset-x-10 bottom-4 h-16 rounded-[100%] bg-binky-blue/25 blur-2xl" />
          <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_50%_40%,rgba(240,62,155,0.16),transparent_62%)]" />
          <motion.img
            src="/assets/binky-pack-pink.png"
            alt="Mi Binky Comfort System retail pack — pink pacifier with cotton inserts, comfort drops and tweezers"
            data-testid="hero-pack-image"
            initial={{ opacity: 0, y: 60, rotate: 2 }}
            animate={{ opacity: 1, rotate: 0, y: [0, -12, 0] }}
            transition={{
              opacity: { duration: 0.8, delay: 0.3 },
              rotate: { duration: 1, delay: 0.3 },
              y: { duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 },
            }}
            className="relative z-10 mx-auto max-h-[380px] w-auto object-contain drop-shadow-[0_40px_60px_rgba(27,79,160,0.28)] sm:max-h-[460px] lg:max-h-[560px]"
          />
          <div className="hidden lg:contents">
            <Chip testid="hero-chip-bpa" className="left-0 top-[14%]" delay={1.1}>
              <ShieldCheck className="h-4 w-4 text-binky-green" /> BPA Free &amp; Non-Toxic
            </Chip>
            <Chip testid="hero-chip-drops" className="right-0 top-[38%] sm:right-[-2%]" delay={1.3}>
              <Droplets className="h-4 w-4 text-binky-pink" /> 15 ml Comfort Drops Included
            </Chip>
            <Chip testid="hero-chip-cotton" className="bottom-[12%] left-[2%]" delay={1.5}>
              <Heart className="h-4 w-4 fill-binky-pink text-binky-pink" /> 100% Cotton Inserts
            </Chip>
          </div>
        </motion.div>

        <div className="lg:self-start">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="flex flex-wrap items-center gap-3 sm:gap-4"
          >
            <button
              data-testid="hero-cta-how-it-works"
              onClick={() => scrollToId("#how-it-works")}
              className="rounded-full border-2 border-binky-blue/15 bg-white/80 px-6 py-3 text-sm font-bold text-binky-blue backdrop-blur transition-all duration-300 hover:border-binky-blue hover:shadow-lg hover:shadow-binky-blue/10 sm:px-7 sm:py-3.5"
            >
              How It Works
            </button>
            <button
              data-testid="hero-cta-explore"
              onClick={() => scrollToId("#pack")}
              className="rounded-full bg-binky-blue px-6 py-3 text-sm font-bold text-white shadow-lg shadow-binky-blue/30 transition-transform duration-300 hover:scale-[1.04] active:scale-95 sm:px-7 sm:py-3.5"
            >
              Explore Mi Binky
            </button>
            <button
              data-testid="hero-cta-register"
              onClick={() => scrollToId("#register")}
              className="group inline-flex items-center gap-2 rounded-full bg-binky-pink px-6 py-3 text-sm font-bold text-white shadow-lg shadow-binky-pink/30 transition-transform duration-300 hover:scale-[1.04] active:scale-95 sm:px-7 sm:py-3.5"
            >
              <Heart className="h-4 w-4 fill-current transition-transform duration-300 group-hover:scale-125" />
              Register Your Interest
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-7 flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400 lg:mt-10"
            data-testid="hero-trust-row"
          >
            <span>Designed in Britain</span>
            <span className="text-binky-pink">•</span>
            <span>Patent Pending</span>
            <span className="text-binky-pink">•</span>
            <span>BPA Free &amp; Non-Toxic</span>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
